import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryCard from '@/components/base/CategoryCard';
import SEOHead from '@/components/base/SEOHead';
import { categories, allArticles, faqs } from '@/mocks/articles';

const HERO_IMAGE = "https://readdy.ai/api/search-image?query=A%20warm%20abstract%20digital%20security%20concept%20with%20soft%20glowing%20shield%20and%20lock%20shapes%20floating%20in%20a%20gentle%20gradient%20space%20minimal%20clean%20design%20warm%20amber%20and%20cream%20tones%20modern%20editorial%20style%20high%20detail&width=1800&height=900&seq=hero-home-safety&orientation=landscape";

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const featuredArticles = allArticles.slice(0, 6);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '成人网站安全使用完全指南',
    url: 'https://safety-guide.example.com',
    description: '本页围绕成人网站提供全面实用信息，涵盖如何安全访问成人网站、辨别正规平台、保护个人隐私及防范风险。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://safety-guide.example.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '安全指南',
    url: 'https://safety-guide.example.com',
    description: '为成年用户提供专业、可靠的内容安全指导',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="成人网站完全指南：安全访问与内容选择"
        description="本页围绕成人网站提供全面实用信息，涵盖如何安全访问成人网站、辨别正规平台、保护个人隐私及防范风险，帮助成年用户做出明智、安全的内容选择决策。"
        keywords="成人网站,成人网站安全访问,正规成人网站推荐,成人网站隐私保护,成人内容平台选择,防范成人网站风险,成人网站使用指南"
        canonicalUrl="https://safety-guide.example.com"
        ogImage={HERO_IMAGE}
        schemaJson={{ ...homeSchema, ...orgSchema }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
            <img
              src={HERO_IMAGE}
              alt="成人网站安全使用完全指南"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30" />
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight max-w-3xl mx-auto">
                成人网站安全使用完全指南
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed">
                为成年用户提供全面的安全访问指导，涵盖隐私保护、正规平台识别、风险防范与法律合规等关键信息，助您做出明智安全的内容选择。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/safe-access"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line" />
                  开始了解安全指南
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 text-white border border-white/25 rounded-md font-medium text-sm hover:bg-background-50/25 transition-colors whitespace-nowrap backdrop-blur-sm"
                >
                  <i className="ri-information-line" />
                  关于我们
                </Link>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  内容分类导航
                </h2>
                <p className="text-base text-foreground-600 max-w-xl mx-auto">
                  五大核心分类，全面覆盖成人网站安全使用的各个方面
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <CategoryCard key={cat.slug} {...cat} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    精选文章
                  </h2>
                  <p className="text-base text-foreground-600">
                    由专业团队精心编写，助您全面了解安全知识
                  </p>
                </div>
                <Link
                  to="/category/safe-access"
                  className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  查看全部文章
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-base text-foreground-600">
                  关于成人网站安全使用的最常见疑问解答
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-background-100/50 transition-colors"
                    >
                      <span className="font-medium text-sm text-foreground-900">
                        {faq.question}
                      </span>
                      <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-500 transition-transform duration-200 ${activeFaq === index ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line" />
                      </span>
                    </button>
                    {activeFaq === index && (
                      <div className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed animate-in slide-in-from-top-1 duration-200">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-accent-500">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                开始您的安全之旅
              </h2>
              <p className="text-base text-background-100/90 mb-8 max-w-xl mx-auto leading-relaxed">
                知识是保护自己的第一道防线。浏览我们的分类指南，了解如何安全、负责任地使用成人内容网站。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/privacy-protection"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-accent-600 rounded-md font-medium text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-lock-line" />
                  隐私保护指南
                </Link>
                <Link
                  to="/category/risk-prevention"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-accent-600 text-background-50 rounded-md font-medium text-sm hover:bg-accent-700 transition-colors whitespace-nowrap border border-accent-400/30"
                >
                  <i className="ri-alert-line" />
                  风险防范手册
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}